import {
  decompose,
  getMostSpecificValue,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { AtomicValueWithNesting } from '../abstractStyles/types';

/**
 * Normalizes a CSS value by treating 'spx' (scaled pixels) as 'px' (pixels).
 * This is needed because the platform may return values in spx but presets use px.
 *
 * @param value - CSS value to normalize
 * @returns Normalized value with spx converted to px
 */
function normalizeValue(value: string): string {
  return value.replace(/spx\b/g, 'px');
}

/**
 * Checks if a preset is currently active by comparing its decomposed atomics
 * with the current decomposed values.
 *
 * A preset is considered active if ALL of its atomic values match
 * the current values (checking the most specific value at nesting level 0).
 *
 * @param presetValues - Preset values to check (can be shorthand or atomic)
 * @param currentDecomposed - Current decomposed atomic values with nesting
 * @returns true if preset is currently active
 */
export function isPresetActive(
  presetValues: Record<string, any>,
  currentDecomposed: Record<string, AtomicValueWithNesting[]>,
): boolean {
  // Decompose all preset values to atomics
  const presetAtomics: Record<string, string> = {};

  for (const [property, value] of Object.entries(presetValues)) {
    const decomposed = decompose(property, String(value));

    if (decomposed) {
      // Property is shorthand - add all atomics
      for (const [atomicKey, atomicValue] of Object.entries(decomposed)) {
        if (atomicValue !== null) {
          presetAtomics[atomicKey] = atomicValue;
        }
      }
    } else {
      // Property is already atomic
      presetAtomics[property] = String(value);
    }
  }

  // Check if all preset atomics match current values
  for (const [atomicKey, presetValue] of Object.entries(presetAtomics)) {
    const currentValues = currentDecomposed[atomicKey];

    if (!currentValues || currentValues.length === 0) {
      // Current doesn't have this atomic
      return false;
    }

    // Get the most specific value (lowest nesting level)
    const mostSpecific = getMostSpecificValue(currentValues);

    if (!mostSpecific) {
      return false;
    }

    // Normalize both values to treat spx and px as equal
    const normalizedPresetValue = normalizeValue(presetValue);
    const normalizedCurrentValue = normalizeValue(mostSpecific.resolvedValue);

    if (normalizedCurrentValue !== normalizedPresetValue) {
      // Values don't match
      return false;
    }
  }

  return true;
}

/**
 * Finds the ID of the currently active preset, if any.
 *
 * @param presets - Array of presets to check
 * @param currentDecomposed - Current decomposed atomic values
 * @returns The ID of the active preset, or null if none match
 */
export function findActivePreset<
  T extends { id: string; values: Record<string, any> },
>(
  presets: T[],
  currentDecomposed: Record<string, AtomicValueWithNesting[]>,
): string | null {
  for (const preset of presets) {
    if (isPresetActive(preset.values, currentDecomposed)) {
      return preset.id;
    }
  }
  return null;
}
